import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  PRIMARY_OUTLET,
  RouterStateSnapshot,
  TitleStrategy,
} from '@angular/router';

/** Product name every browser tab title is prefixed with. */
export const BRAND = 'INSCYD';

/** Separator between the brand and the page name — `INSCYD - Page name`. */
const SEPARATOR = ' - ';

/** URL segments that carry no meaning as a label (ids from `:athleteId`, `:testId`, …). */
const OPAQUE_SEGMENT = /^\d+$/;

function humanize(segment: string): string {
  return segment.charAt(0).toUpperCase() + segment.slice(1).replaceAll('-', ' ');
}

function readBreadcrumb(route: ActivatedRouteSnapshot): string | undefined {
  const breadcrumb: unknown = route.data['breadcrumb'];
  return typeof breadcrumb === 'string' ? breadcrumb : undefined;
}

/**
 * Sets `INSCYD - <page name>` on every navigation.
 *
 * The page name is the deepest primary-outlet route that declares a `title`, falling
 * back to that route's `data.breadcrumb` and finally to a humanised URL segment — the
 * same resolution order the topbar breadcrumbs use — so a route that has not declared
 * a title yet still gets a sensible one instead of inheriting the previous page's.
 *
 * Only the primary outlet is walked: the deep-linkable `modal` / `dialog` outlet routes
 * leave the underlying page title alone when they open.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const page = this.buildTitle(snapshot);
    this.title.setTitle(page ? `${BRAND}${SEPARATOR}${page}` : BRAND);
  }

  override buildTitle(snapshot: RouterStateSnapshot): string | undefined {
    let route: ActivatedRouteSnapshot | undefined = snapshot.root;
    let resolved: string | undefined;
    let segment: string | undefined;

    while (route) {
      resolved = route.title ?? readBreadcrumb(route) ?? resolved;

      const path = route.url.at(-1)?.path;
      if (path && !OPAQUE_SEGMENT.test(path)) segment = path;

      route = route.children.find((child) => child.outlet === PRIMARY_OUTLET);
    }

    if (resolved) return resolved;

    return segment ? humanize(segment) : undefined;
  }
}
